"use client";

import { useEffect, useState, Suspense } from "react";
import { useSearchParams } from "next/navigation";
import { Button } from "@/ui/button";
import { Alert } from "@/ui/alert";
import { AlertDescription } from "@/ui/alert";
import { Heading } from "@/ui/heading";
import { Text } from "@/ui/text";
import { LOGIN_SCOPES } from "@/lib/auth/scopes";

function LoginSurface({ children }: { children?: React.ReactNode }) {
  const SURFACE_BLOB_TEXTURE =
    "[background-image:radial-gradient(ellipse_at_25%_30%,rgb(0_0_0_/_0.15)_0%,transparent_50%),radial-gradient(ellipse_at_72%_35%,rgb(0_0_0_/_0.12)_0%,transparent_48%),radial-gradient(ellipse_at_45%_72%,rgb(0_0_0_/_0.14)_0%,transparent_52%)]";

  const SURFACE_BLOB_CLASS =
    "bg-primary/30 motion-safe:[animation:pulse_5.5s_cubic-bezier(0.4,0,0.2,1)_infinite] absolute left-1/2 h-72 w-72 -translate-x-1/2 rounded-full blur-3xl opacity-100";
  return (
    <div className="bg-background relative flex h-full items-center justify-center overflow-hidden px-4">
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-24 w-24 -translate-x-1/2 -translate-y-1/2" />
      <div className={`${SURFACE_BLOB_CLASS} ${SURFACE_BLOB_TEXTURE}`} />
      <div className="relative w-full max-w-md">{children}</div>
    </div>
  );
}

function LoginContent() {
  const searchParams = useSearchParams();
  const errorParam = searchParams.get("error");
  const [error] = useState<string | null>(errorParam);

  useEffect(() => {
    if (errorParam) {
      return;
    }

    const tenantId =
      process.env.NEXT_PUBLIC_AZURE_TENANT_ID || "65d51a1f-8c7c-4535-89ba-43a73aae6c6f";
    // Defaults to machina-architect-dev to match the MAP scope default in scopes.ts.
    // A missing value must never fall back to the production app registration.
    const clientId =
      process.env.NEXT_PUBLIC_AZURE_CLIENT_ID || "3bccffde-4214-44ec-a5f4-c2a2a9bc139d";
    const baseUrl = process.env.NEXT_PUBLIC_URL || window.location.origin;
    const redirectUri = `${baseUrl}/api/auth/callback`;

    const redirectTo = searchParams.get("redirect") || "/";
    const state = btoa(JSON.stringify({ redirectTo }));

    const authUrl = new URL(`https://login.microsoftonline.us/${tenantId}/oauth2/v2.0/authorize`);
    authUrl.searchParams.set("client_id", clientId);
    authUrl.searchParams.set("response_type", "code");
    authUrl.searchParams.set("redirect_uri", redirectUri);
    authUrl.searchParams.set("response_mode", "query");
    authUrl.searchParams.set("scope", LOGIN_SCOPES.join(" "));
    authUrl.searchParams.set("state", state);

    console.log("[Login] Redirecting to Azure AD...");
    window.location.href = authUrl.toString();
  }, [searchParams, errorParam]);

  if (error) {
    return (
      <LoginSurface>
        <div className="text-center">
          <div className="border-b border-primary/20 pb-2">
            <Heading level="h1" className="text-primary-text text-4xl font-semibold text-shadow-lg">
              Architect
            </Heading>
            <Text size="sm" color="muted" className="text-shadow-md">
              RoboForming Studio
            </Text>
          </div>
          <Text size="sm" color="muted" className="pt-2 text-shadow-md">
            Authentication Error
          </Text>
        </div>

        <div className="pt-2">
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>

          <Button className="w-full" onClick={() => (window.location.href = "/login")}>
            Try Again
          </Button>
        </div>
      </LoginSurface>
    );
  }

  return (
    <LoginSurface>
      <div className="text-center gap-4 flex flex-col">
        <div className="border-b border-primary/20 pb-2">
          <Heading
            level="h1"
            className="text-primary-text text-4xl font-semibold text-shadow-lg [--tw-text-shadow-color:rgb(0_0_0_/_0.45)]"
          >
            Architect
          </Heading>
          <Text
            size="sm"
            color="muted"
            className="text-shadow-md [--tw-text-shadow-color:rgb(0_0_0_/_0.4)]"
          >
            RoboForming Studio
          </Text>
        </div>
        <Text size="sm" className="text-shadow-md [--tw-text-shadow-color:rgb(0_0_0_/_0.4)]">
          Redirecting to Azure AD...
        </Text>
      </div>
    </LoginSurface>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={<LoginSurface />}>
      <LoginContent />
    </Suspense>
  );
}
